import React, { useState, useCallback } from 'react';
import { getDatabase, ref, set, push } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { Button, Form, Alert, Modal } from 'react-bootstrap';
import { Upload, X, Image, Library } from 'lucide-react';
import './BuildingCheckTemplatesEditor.css';
import { useDragDrop } from '../../hooks/useDragDrop';
import { UploadPickerModal } from '../../Components/UploadPickerModal';
import {
    BuildingCheckTemplate,
    BuildingCheckTemplateFormData,
    INTERVAL_UNITS,
    NEXT_DUE_DATE_OPTIONS,
    TEMPLATE_CATEGORIES,
    REGULATORY_TYPE_OPTIONS,
    TemplateCategory,
    InspectionCategory,
    RegulatoryType
} from './types';
import { PhotoModel } from '../../types/protocols';
import { formatDateForStorage } from '../../Helpers/DateUtils';
import { tombstoneAttachmentDiff } from '../../api/fileDeletionApi';
import { recordActivity } from '../../api/activityApi';
import { ASSESSMENT_GROUP_KEYS, ASSESSMENT_GROUPS, AssessmentGroupKey } from '../../Helpers/assessmentGroups';
import NumberInput from '../../Components/NumberInput';
import { LibraryPickerModal } from '../../Components/LibraryPickerModal';
import { inspectionLibrary } from '../../library/inspectionLibrary';
import { LibraryInspectionTemplate } from '../../types/LibraryInspectionTemplate';
import ComponentTypeSelect from './components/ComponentTypeSelect';

type LinkMode = 'none' | 'type' | 'group';

interface BuildingCheckTemplateModalProps {
    /** The template to edit; null creates a new one */
    template: BuildingCheckTemplate | null;
    /**
     * New templates only: the template cannot be Skickbedömning (no condition
     * assessment category, no assessment group), but may suggest a component type.
     * Used where the template is created for ordinary checks, e.g. "Lägg till aktivitet"
     * and the maintenance draft.
     */
    excludeAssessment?: boolean;
    /** New templates only: the suggested component type to start from (the draft's selected components) */
    initialSpecificType?: string;
    /** Shown above the form */
    infoText?: string;
    onSaved: (template: BuildingCheckTemplate) => void;
    onCancel: () => void;
}

const formDataFrom = (template: BuildingCheckTemplate | null): BuildingCheckTemplateFormData => template ? {
    name: template.name,
    category: template.category || InspectionCategory.MeterReadings,
    description: template.description,
    componentSpecificType: template.componentSpecificType,
    componentBaseTypeGroup: template.componentBaseTypeGroup,
    system: template.system,
    isRegulatoryRequirement: !!template.regulatoryType,
    regulatoryType: template.regulatoryType,
    estimatedTime: template.estimatedTime,
    takesMeasurement: {
        enabled: !!template.takesMeasurement,
        metric: template.takesMeasurement?.metric || 'mm',
        hasMinValue: template.takesMeasurement?.acceptableRange?.min !== undefined,
        hasMaxValue: template.takesMeasurement?.acceptableRange?.max !== undefined,
        minValue: template.takesMeasurement?.acceptableRange?.min ?? 0,
        maxValue: template.takesMeasurement?.acceptableRange?.max ?? 100
    },
    instructionalAttachments: template.instructionalAttachments || {},
    interval: template.interval,
    nextDueDateCalculation: template.nextDueDateCalculation,
    photoRequired: template.photoRequired,
    estimatedCost: template.estimatedCost,
    advanceNotice: template.advanceNotice
} : {
    name: '',
    category: InspectionCategory.MeterReadings,
    description: '',
    isRegulatoryRequirement: false,
    regulatoryType: undefined,
    estimatedTime: 0,
    takesMeasurement: {
        enabled: false,
        metric: 'mm',
        hasMinValue: false,
        hasMaxValue: false,
        minValue: 0,
        maxValue: 100
    },
    instructionalAttachments: {},
    interval: {
        value: 1,
        unit: 'months'
    },
    nextDueDateCalculation: 'scheduled',
    photoRequired: false,
    estimatedCost: 0,
    advanceNotice: 30
};

/**
 * Create or edit a building check template (`inspections/templates/{id}`).
 * Mount it to open it. Used by the Settings tab and the maintenance draft.
 */
const BuildingCheckTemplateModal: React.FC<BuildingCheckTemplateModalProps> = ({
    template: editingTemplate,
    excludeAssessment = false,
    initialSpecificType,
    infoText,
    onSaved,
    onCancel
}) => {
    const db = getDatabase();
    const storage = getStorage();
    const auth = getAuth();

    // Pre-minted key for new templates so attachments upload to the template's
    // own Storage path before the template exists (no shared temp/ folder)
    const [draftTemplateId] = useState<string | null>(() =>
        editingTemplate ? null : push(ref(db, 'inspections/templates')).key
    );
    const [formData, setFormData] = useState<BuildingCheckTemplateFormData>(() => formDataFrom(editingTemplate));
    // Component linking: none, a suggested component type (pre-filters the picker when checks
    // are added) or an assessment group (Skickbedömning; only the three system templates use it)
    const startType = editingTemplate ? editingTemplate.componentSpecificType : initialSpecificType;
    const [linkMode, setLinkMode] = useState<LinkMode>(
        editingTemplate?.componentBaseTypeGroup ? 'group' : startType ? 'type' : 'none'
    );
    const [selectedSpecificType, setSelectedSpecificType] = useState<string | null>(startType || null);
    const [selectedAssessmentGroup, setSelectedAssessmentGroup] = useState<AssessmentGroupKey | null>(
        editingTemplate?.componentBaseTypeGroup || null
    );
    const [saving, setSaving] = useState(false);
    const [showValidation, setShowValidation] = useState(false);
    const [modalAlert, setModalAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showLibraryPicker, setShowLibraryPicker] = useState(false);

    const isSystemTemplate = !!editingTemplate?.system;
    const isRegulatoryRequirement = formData.isRegulatoryRequirement;
    const categoryOptions = excludeAssessment
        ? TEMPLATE_CATEGORIES.filter(c => c.value !== InspectionCategory.ConditionAssessment)
        : TEMPLATE_CATEGORIES;

    const changeLinkMode = (mode: LinkMode): void => {
        setLinkMode(mode);
        // Clear the selections that no longer apply
        if (mode !== 'type') setSelectedSpecificType(null);
        if (mode !== 'group') setSelectedAssessmentGroup(null);
    };

    /**
     * Copy a kontroll-library entry into the form: overwrites every field the
     * library carries, keeps attachments. The name is the entry's Swedish one
     * (the web has no other UI language). The entry's component type becomes the suggestion.
     */
    const handleLibrarySelect = (entry: LibraryInspectionTemplate): void => {
        const measurement = entry.takesMeasurement;
        setFormData(prev => ({
            ...prev,
            name: entry.translations.sv,
            description: entry.description,
            category: entry.category,
            isRegulatoryRequirement: entry.regulatoryType !== null,
            regulatoryType: entry.regulatoryType ?? undefined,
            estimatedTime: entry.estimatedTime,
            takesMeasurement: {
                enabled: measurement !== null,
                metric: measurement?.metric ?? prev.takesMeasurement.metric,
                hasMinValue: measurement?.acceptableRange?.min !== undefined,
                hasMaxValue: measurement?.acceptableRange?.max !== undefined,
                minValue: measurement?.acceptableRange?.min ?? prev.takesMeasurement.minValue,
                maxValue: measurement?.acceptableRange?.max ?? prev.takesMeasurement.maxValue
            },
            interval: { ...entry.interval },
            nextDueDateCalculation: entry.nextDueDateCalculation,
            photoRequired: entry.photoRequired,
            estimatedCost: entry.estimatedCost,
            advanceNotice: entry.advanceNotice
        }));
        if (entry.componentSpecificType) {
            setLinkMode('type');
            setSelectedSpecificType(entry.componentSpecificType);
            setSelectedAssessmentGroup(null);
        } else {
            changeLinkMode('none');
        }
        setShowLibraryPicker(false);
    };

    // Form validation
    const isFormValid = (): boolean => {
        const isRegulatoryValid = !isRegulatoryRequirement || formData.regulatoryType !== undefined;
        return (
            formData.name.trim() !== '' &&
            formData.description.trim() !== '' &&
            formData.interval.value > 0 &&
            isRegulatoryValid
        );
    };

    // Check if a specific field is valid
    const isFieldValid = (fieldName: string): boolean => {
        switch (fieldName) {
            case 'name':
                return formData.name.trim() !== '';
            case 'description':
                return formData.description.trim() !== '';
            case 'interval':
                return formData.interval.value > 0;
            case 'regulatoryType':
                return !isRegulatoryRequirement || formData.regulatoryType !== undefined;
            default:
                return true;
        }
    };

    // Check if a field should show validation
    const shouldShowValidation = (fieldName: string): boolean => {
        if (!showValidation) return false;
        return !isFieldValid(fieldName);
    };

    // Save template to Firebase
    const saveTemplate = async (): Promise<void> => {
        if (!isFormValid()) {
            setShowValidation(true);
            setModalAlert({ type: 'danger', message: 'Vänligen fyll i alla obligatoriska fält.' });
            return;
        }

        try {
            setSaving(true);
            const now = formatDateForStorage(new Date());
            const templateId = editingTemplate?.id || draftTemplateId;
            if (!templateId) {
                throw new Error('Mall-ID saknas');
            }

            const componentSpecificType = linkMode === 'type' ? selectedSpecificType : null;
            const componentBaseTypeGroup = !excludeAssessment && linkMode === 'group' ? selectedAssessmentGroup : null;
            // System templates keep their category and group whatever the form says (D5)
            const lockedCategory = editingTemplate?.system ? editingTemplate.category : undefined;
            const template: BuildingCheckTemplate = {
                id: templateId,
                name: formData.name,
                category: lockedCategory || formData.category || InspectionCategory.MeterReadings,
                ...(componentSpecificType && { componentSpecificType }),
                ...(componentBaseTypeGroup && { componentBaseTypeGroup }),
                ...(editingTemplate?.system && { system: true }),
                ...(formData.isRegulatoryRequirement && formData.regulatoryType && { regulatoryType: formData.regulatoryType }),
                description: formData.description,
                estimatedTime: formData.estimatedTime,
                instructionalAttachments: formData.instructionalAttachments,
                interval: formData.interval,
                nextDueDateCalculation: formData.nextDueDateCalculation,
                photoRequired: formData.photoRequired,
                estimatedCost: formData.estimatedCost,
                advanceNotice: formData.advanceNotice,
                createdAt: editingTemplate?.createdAt || now,
                updatedAt: now
            };

            if (formData.takesMeasurement.enabled) {
                const takesMeasurement: NonNullable<BuildingCheckTemplate['takesMeasurement']> = {
                    metric: formData.takesMeasurement.metric
                };

                if (formData.takesMeasurement.hasMinValue || formData.takesMeasurement.hasMaxValue) {
                    const acceptableRange: { min?: number; max?: number } = {};

                    if (formData.takesMeasurement.hasMinValue) {
                        acceptableRange.min = formData.takesMeasurement.minValue;
                    }

                    if (formData.takesMeasurement.hasMaxValue) {
                        acceptableRange.max = formData.takesMeasurement.maxValue;
                    }

                    takesMeasurement.acceptableRange = acceptableRange;
                }

                template.takesMeasurement = takesMeasurement;
            }

            const templateRef = ref(db, `inspections/templates/${templateId}`);
            await set(templateRef, template);
            // Templates are instance-wide configuration.
            recordActivity('settings.updated', { targetKey: templateId, label: template.name });

            // Soft-delete blobs for attachments this edit removed
            await tombstoneAttachmentDiff(
                'inspectionTemplateAttachment',
                editingTemplate?.instructionalAttachments,
                template.instructionalAttachments
            );

            onSaved(template);
        } catch (error) {
            console.error('Error saving template:', error);
            setModalAlert({ type: 'danger', message: 'Fel vid sparande av mall' });
        } finally {
            setSaving(false);
        }
    };

    // Attachment handling functions
    const handleFileUpload = async (file: File, templateId: string | null): Promise<void> => {
        if (!auth.currentUser) {
            setModalAlert({ type: 'danger', message: 'Du måste vara inloggad för att ladda upp filer' });
            return;
        }
        if (!templateId) {
            setModalAlert({ type: 'danger', message: 'Fel vid uppladdning av fil' });
            return;
        }

        try {
            setSaving(true);

            // Storage objects are write-once — each upload gets a fresh
            // push-key path under its template
            const attachmentKey = push(ref(db, 'inspections/templates')).key;
            if (!attachmentKey) {
                throw new Error('Kunde inte skapa nyckel för bilagan');
            }

            const storagePath = `inspections/templates/${templateId}/instructionalAttachments/${attachmentKey}`;
            const fileRef = storageRef(storage, storagePath);

            await uploadBytes(fileRef, file);

            const photoModel: PhotoModel = {
                key: file.name,
                author: auth.currentUser.uid,
                date: formatDateForStorage(new Date()),
                width: 0,
                height: 0,
                storagePath: storagePath,
                fileSize: file.size
            };

            setFormData(prev => ({
                ...prev,
                instructionalAttachments: {
                    ...prev.instructionalAttachments,
                    [attachmentKey]: photoModel
                }
            }));

        } catch (error) {
            console.error('Error uploading file:', error);
            setModalAlert({ type: 'danger', message: 'Fel vid uppladdning av fil' });
        } finally {
            setSaving(false);
        }
    };

    const removeAttachment = (dbKey: string): void => {
        // An unsaved upload loses its only reference here — tombstone the blob
        // right away. Persisted attachments are tombstoned when the removal is
        // actually saved.
        const attachment = formData.instructionalAttachments[dbKey];
        if (attachment && !editingTemplate?.instructionalAttachments?.[dbKey]) {
            void tombstoneAttachmentDiff('inspectionTemplateAttachment', { [dbKey]: attachment }, undefined);
        }

        setFormData(prev => {
            const newAttachments = { ...prev.instructionalAttachments };
            delete newAttachments[dbKey];
            return {
                ...prev,
                instructionalAttachments: newAttachments
            };
        });
    };

    const cancel = (): void => {
        // Unsaved uploads are abandoned with the modal — tombstone their blobs
        void tombstoneAttachmentDiff(
            'inspectionTemplateAttachment',
            formData.instructionalAttachments,
            editingTemplate?.instructionalAttachments
        );
        onCancel();
    };

    // Handle selected files (from upload modal or drag-and-drop)
    const handleFiles = useCallback((files: File[]) => {
        if (files.length === 0) return;

        const file = files[0];

        if (file.size > 10 * 1024 * 1024) {
            setModalAlert({ type: 'danger', message: 'Filen är för stor. Maximal storlek är 10MB' });
            return;
        }

        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain'];
        if (!allowedTypes.includes(file.type)) {
            setModalAlert({ type: 'danger', message: 'Filtypen stöds inte. Tillåtna format: JPEG, PNG, GIF, WebP, PDF, TXT' });
            return;
        }

        handleFileUpload(file, editingTemplate?.id || draftTemplateId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingTemplate?.id, draftTemplateId]);

    const { isDragOver, dragHandlers } = useDragDrop({ onFilesDropped: handleFiles });

    return (
        <>
            <Modal show onHide={cancel} size="lg" dialogClassName="building-checks-editor-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{editingTemplate ? 'Redigera mall' : 'Ny mall'}</Modal.Title>
                    {!isSystemTemplate && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm ms-auto me-3"
                            onClick={() => setShowLibraryPicker(true)}
                            disabled={saving}
                        >
                            <Library size={14} className="me-1" />
                            Kopiera från bibliotek
                        </button>
                    )}
                </Modal.Header>
                <Modal.Body>
                    {infoText && (
                        <Alert variant="info" className="mb-3">
                            {infoText}
                        </Alert>
                    )}
                    {modalAlert && (
                        <Alert variant={modalAlert.type} className="mb-3">
                            {modalAlert.message}
                        </Alert>
                    )}
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Namn *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="T.ex. Brandskyddskontroll"
                                isInvalid={shouldShowValidation('name')}
                                isValid={showValidation && isFieldValid('name') && formData.name.trim() !== ''}
                            />
                            {shouldShowValidation('name') && (
                                <Form.Control.Feedback type="invalid">
                                    Namn är obligatoriskt
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Kategori</Form.Label>
                            <Form.Select
                                value={formData.category}
                                disabled={isSystemTemplate}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    category: e.target.value as TemplateCategory
                                }))}
                            >
                                {categoryOptions.map(cat => (
                                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                                ))}
                            </Form.Select>
                            {isSystemTemplate && (
                                <Form.Text className="text-muted">
                                    Systemmall för skickbedömning: kategori och komponentgrupp är låsta. Intervall och beskrivning kan ändras.
                                </Form.Text>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="regulatory-requirement-toggle"
                                label="Myndighetskrav"
                                checked={isRegulatoryRequirement}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setFormData(prev => ({
                                        ...prev,
                                        isRegulatoryRequirement: checked,
                                        regulatoryType: checked ? prev.regulatoryType : undefined
                                    }));
                                }}
                                className="mb-2"
                            />
                            {isRegulatoryRequirement && (
                                <div className="ms-4">
                                    <Form.Select
                                        value={formData.regulatoryType || ''}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            regulatoryType: e.target.value as RegulatoryType || undefined
                                        }))}
                                        isInvalid={shouldShowValidation('regulatoryType')}
                                        isValid={showValidation && isFieldValid('regulatoryType')}
                                    >
                                        <option value="">Välj typ av myndighetskrav...</option>
                                        {REGULATORY_TYPE_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </Form.Select>
                                    {shouldShowValidation('regulatoryType') && (
                                        <Form.Control.Feedback type="invalid" className="d-block">
                                            Välj typ av myndighetskrav
                                        </Form.Control.Feedback>
                                    )}
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Beskrivning *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                placeholder="Skriv instruktion här."
                                isInvalid={shouldShowValidation('description')}
                                isValid={showValidation && isFieldValid('description') && formData.description.trim() !== ''}
                            />
                            {shouldShowValidation('description') && (
                                <Form.Control.Feedback type="invalid">
                                    Beskrivning är obligatorisk
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Koppla till komponenter</Form.Label>
                            <Form.Select
                                value={linkMode}
                                disabled={isSystemTemplate}
                                onChange={(e) => changeLinkMode(e.target.value as LinkMode)}
                                className="mb-2"
                            >
                                <option value="none">Ingen koppling</option>
                                <option value="type">Föreslagen komponenttyp</option>
                                {!excludeAssessment && (
                                    <option value="group">Komponentgrupp (skickbedömning)</option>
                                )}
                            </Form.Select>
                            {linkMode === 'group' && (
                                <div className="component-selector-section">
                                    <Form.Select
                                        value={selectedAssessmentGroup ?? ''}
                                        disabled={isSystemTemplate}
                                        onChange={(e) => setSelectedAssessmentGroup((e.target.value as AssessmentGroupKey) || null)}
                                    >
                                        <option value="">Välj komponentgrupp...</option>
                                        {ASSESSMENT_GROUP_KEYS.map(group => (
                                            <option key={group} value={group}>{ASSESSMENT_GROUPS[group].label}</option>
                                        ))}
                                    </Form.Select>
                                    {selectedAssessmentGroup && (
                                        <Form.Text className="text-muted d-block mt-1">
                                            Ingår: {ASSESSMENT_GROUPS[selectedAssessmentGroup].membersDescription}. Kopplas automatiskt till alla aktiva komponenter i gruppen i varje byggnad; bedömningen utförs i appen.
                                        </Form.Text>
                                    )}
                                </div>
                            )}
                            {linkMode === 'type' && (
                                <div className="component-selector-section">
                                    <ComponentTypeSelect
                                        value={selectedSpecificType}
                                        disabled={isSystemTemplate}
                                        onChange={setSelectedSpecificType}
                                        noneLabel="Välj komponenttyp..."
                                    />
                                    <Form.Text className="text-muted d-block mt-1">
                                        Förval när kontroller av mallen läggs till på en byggnad: komponenter av typen föreslås, en kontroll per komponent. Andra komponenter kan också väljas.
                                    </Form.Text>
                                </div>
                            )}
                        </Form.Group>

                        <div className="row">
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Beräknad arbetstid (minuter)</Form.Label>
                                    <NumberInput
                                        className="form-control"
                                        min="0"
                                        integer
                                        value={formData.estimatedTime}
                                        onChange={(value) => setFormData(prev => ({ ...prev, estimatedTime: value }))}
                                        placeholder="0"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Beräknad kostnad (kr)</Form.Label>
                                    <NumberInput
                                        className="form-control"
                                        min="0"
                                        integer
                                        value={formData.estimatedCost}
                                        onChange={(value) => setFormData(prev => ({ ...prev, estimatedCost: value }))}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Förvarning (dagar)</Form.Label>
                                    <NumberInput
                                        className="form-control"
                                        min="0"
                                        integer
                                        value={formData.advanceNotice}
                                        onChange={(value) => setFormData(prev => ({ ...prev, advanceNotice: value }))}
                                    />
                                </Form.Group>
                            </div>
                        </div>

                        {/* Interval */}
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Intervall *</Form.Label>
                                    <div className="d-flex gap-2">
                                        <NumberInput
                                            className="form-control"
                                            min="1"
                                            integer
                                            value={formData.interval.value}
                                            onChange={(value) => setFormData(prev => ({
                                                ...prev,
                                                interval: { ...prev.interval, value }
                                            }))}
                                            style={{ width: '100px' }}
                                            isInvalid={shouldShowValidation('interval')}
                                            isValid={showValidation && isFieldValid('interval') && formData.interval.value > 0}
                                        />
                                        <Form.Select
                                            value={formData.interval.unit}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                interval: { ...prev.interval, unit: e.target.value as BuildingCheckTemplate['interval']['unit'] }
                                            }))}
                                        >
                                            {INTERVAL_UNITS.map(unit => (
                                                <option key={unit.value} value={unit.value}>{unit.label}</option>
                                            ))}
                                        </Form.Select>
                                    </div>
                                    {shouldShowValidation('interval') && (
                                        <Form.Control.Feedback type="invalid" className="d-block">
                                            Intervall måste vara större än 0
                                        </Form.Control.Feedback>
                                    )}
                                </Form.Group>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Nästa förfallodatum beräknas</Form.Label>
                            <Form.Select
                                value={formData.nextDueDateCalculation}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    nextDueDateCalculation: e.target.value as BuildingCheckTemplate['nextDueDateCalculation']
                                }))}
                            >
                                {NEXT_DUE_DATE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>

                        {/* Measurement */}
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Kontrollen tar mätvärde"
                                checked={formData.takesMeasurement.enabled}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    takesMeasurement: { ...prev.takesMeasurement, enabled: e.target.checked }
                                }))}
                            />
                        </Form.Group>

                        {formData.takesMeasurement.enabled && (
                            <div className="row">
                                <div className="col-md-4">
                                    <Form.Group className="mb-3">
                                        <Form.Label>Enhet</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.takesMeasurement.metric}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                takesMeasurement: { ...prev.takesMeasurement, metric: e.target.value }
                                            }))}
                                            placeholder="T.ex. mm, kg, liter, bar, °C"
                                        />
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            label="Har min-värde"
                                            checked={formData.takesMeasurement.hasMinValue}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                takesMeasurement: { ...prev.takesMeasurement, hasMinValue: e.target.checked }
                                            }))}
                                        />
                                        {formData.takesMeasurement.hasMinValue && (
                                            <NumberInput
                                                placeholder="Min värde"
                                                value={formData.takesMeasurement.minValue}
                                                onChange={(value) => setFormData(prev => ({
                                                    ...prev,
                                                    takesMeasurement: { ...prev.takesMeasurement, minValue: value }
                                                }))}
                                                className="form-control mt-2"
                                            />
                                        )}
                                    </Form.Group>
                                </div>
                                <div className="col-md-4">
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            label="Har max-värde"
                                            checked={formData.takesMeasurement.hasMaxValue}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                takesMeasurement: { ...prev.takesMeasurement, hasMaxValue: e.target.checked }
                                            }))}
                                        />
                                        {formData.takesMeasurement.hasMaxValue && (
                                            <NumberInput
                                                placeholder="Max värde"
                                                value={formData.takesMeasurement.maxValue}
                                                onChange={(value) => setFormData(prev => ({
                                                    ...prev,
                                                    takesMeasurement: { ...prev.takesMeasurement, maxValue: value }
                                                }))}
                                                className="form-control mt-2"
                                            />
                                        )}
                                    </Form.Group>
                                </div>
                            </div>
                        )}

                        {/* Attachments Section */}
                        <div
                            {...dragHandlers}
                            style={{
                                padding: '12px',
                                marginBottom: '1rem',
                                borderRadius: '8px',
                                border: isDragOver ? '2px dashed #007bff' : '2px dashed #dee2e6',
                                backgroundColor: isDragOver ? '#f0f8ff' : 'transparent',
                                transition: 'all 0.2s ease'
                            }}
                        >
                        <Form.Group>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <Form.Label className="fw-medium mb-0">Instruktionsbilagor</Form.Label>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() => setShowUploadModal(true)}
                                    disabled={saving}
                                >
                                    <Upload size={14} className="me-1" />
                                    Ladda upp fil
                                </Button>
                            </div>

                            {Object.keys(formData.instructionalAttachments).length === 0 && (
                                <div className="text-muted fst-italic small mb-3">
                                    Inga bilagor bifogade ännu. Du kan också dra och släppa filer här.
                                </div>
                            )}

                            {Object.entries(formData.instructionalAttachments).map(([dbKey, attachment]) => (
                                <div key={dbKey} className="card bg-light border-0 mb-2">
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <Image size={16} className="text-muted me-2" />
                                                <div>
                                                    <div className="fw-medium small">{attachment.key}</div>
                                                    <div className="text-muted small">
                                                        Uppladdad: {new Date(attachment.date).toLocaleDateString('sv-SE')}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => removeAttachment(dbKey)}
                                                className="btn btn-outline-danger btn-sm"
                                                type="button"
                                                disabled={saving}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </Form.Group>
                        </div>

                        {/* Photo requirement */}
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                label="Foto av arbetet krävs"
                                checked={formData.photoRequired}
                                onChange={(e) => setFormData(prev => ({ ...prev, photoRequired: e.target.checked }))}
                            />
                        </Form.Group>

                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={cancel}>
                        Avbryt
                    </Button>
                    <Button
                        variant="primary"
                        onClick={saveTemplate}
                        disabled={saving}
                    >
                        {saving ? 'Sparar...' : 'Spara mall'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <LibraryPickerModal
                config={inspectionLibrary}
                open={showLibraryPicker}
                onClose={() => setShowLibraryPicker(false)}
                onSelect={handleLibrarySelect}
            />

            {/* Upload Picker Modal */}
            <UploadPickerModal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                onFilesSelected={handleFiles}
                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.txt"
                multiple={false}
                title="Ladda upp"
            />
        </>
    );
};

export default BuildingCheckTemplateModal;
